/**
 * Pyramid Store
 * 
 * Manages Max Priorities Pyramid state with Supabase persistence.
 * 
 * PERSISTENCE:
 * - Primary: Supabase (pyramids and pyramid_items tables)
 * - Export: Excel/JSON for sharing and backup
 * 
 * Follows Build Contract Section 2.1 - Supabase is the system of record.
 */

import { create } from 'zustand';
import {
  fetchAll,
  upsertRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';

// ============================================================================
// Types
// ============================================================================

export type TimeHorizon = 'Transform' | 'Grow' | 'Run';
export type Category = string;

export interface PyramidItem {
  id: string;
  pyramidId: string;
  name: string;
  description?: string;
  timeHorizon: TimeHorizon;
  category: Category;
  rank: number;
  linkedCandidateId?: string;
  source?: 'manual' | 'miro' | 'excel' | 'csv' | 'imported';
  externalId?: string;
  createdBy: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Pyramid {
  id: string;
  name: string;
  description?: string;
  categories: Category[];
  createdBy: string;
  workspaceId?: string;
  cycleId?: string;
  sessionId?: string;
  isPublished: boolean;
  publishedAt?: Date;
  publishedBy?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

interface PyramidState {
  // Data
  pyramids: Pyramid[];
  items: PyramidItem[];
  activePyramidId: string | null;
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncPyramidToSupabase: (pyramid: Pyramid) => Promise<{ success: boolean; error?: string }>;
  syncItemToSupabase: (item: PyramidItem) => Promise<{ success: boolean; error?: string }>;
  
  // Pyramid CRUD
  createPyramid: (data: { name: string; description?: string; categories?: Category[]; createdBy: string; workspaceId?: string }) => Promise<string>;
  updatePyramid: (id: string, updates: Partial<Pyramid>) => Promise<void>;
  deletePyramid: (id: string) => Promise<void>;
  setActivePyramid: (id: string | null) => void;
  
  // Item CRUD
  addItem: (pyramidId: string, data: { name: string; description?: string; timeHorizon: TimeHorizon; category: Category; createdBy: string; source?: PyramidItem['source'] }) => Promise<string>;
  updateItem: (itemId: string, updates: Partial<PyramidItem>) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  moveItem: (itemId: string, timeHorizon: TimeHorizon, category: Category, newRank?: number) => Promise<void>;
  updateItemRank: (itemId: string, newRank: number) => Promise<void>;
  
  // Category management
  updateCategories: (pyramidId: string, categories: Category[]) => Promise<void>;
  
  // Bulk operations
  importItems: (pyramidId: string, items: Array<{ 
    name: string; 
    description?: string; 
    timeHorizon: TimeHorizon; 
    category: Category; 
    rank?: number; 
    source?: PyramidItem['source'];
    linkedCandidateId?: string;
    externalId?: string;
    metadata?: Record<string, unknown>;
  }>, createdBy: string) => Promise<void>;
  clearItems: (pyramidId: string) => Promise<void>;
  
  // Getters
  getPyramidById: (id: string) => Pyramid | undefined;
  getItemsForPyramid: (pyramidId: string) => PyramidItem[];
  getItemsForCell: (pyramidId: string, timeHorizon: TimeHorizon, category: Category) => PyramidItem[];
  getActivePyramid: () => Pyramid | undefined;
}

const PYRAMIDS_TABLE = 'pyramids';
const ITEMS_TABLE = 'pyramid_items';

// ============================================================================
// Store
// ============================================================================

export const usePyramidStore = create<PyramidState>()((set, get) => ({
  pyramids: [],
  items: [],
  activePyramidId: null,
  syncState: createInitialSyncState(),
  
  // ========================================================================
  // Supabase Sync
  // ========================================================================
  
  fetchFromSupabase: async () => {
    if (!isSupabaseAvailable()) {
      console.log('[PyramidStore] Supabase not available');
      return;
    }
    
    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
    
    const [pyramidsResult, itemsResult] = await Promise.all([
      fetchAll<Pyramid>(PYRAMIDS_TABLE, { column: 'created_at', ascending: false }, { filterByWorkspace: true }),
      fetchAll<PyramidItem>(ITEMS_TABLE, { column: 'created_at', ascending: true }),
    ]);
    
    if (pyramidsResult.error || itemsResult.error) {
      set({ 
        syncState: { 
          ...get().syncState, 
          isLoading: false, 
          syncError: pyramidsResult.error || itemsResult.error || 'Unknown error'
        } 
      });
      console.error('[PyramidStore] Fetch error:', pyramidsResult.error || itemsResult.error);
      return;
    }
    
    set({
      pyramids: pyramidsResult.data || [],
      items: itemsResult.data || [],
      syncState: {
        ...get().syncState,
        isLoading: false,
        lastSyncAt: new Date(),
        syncError: null,
      },
    });
    
    console.log(`[PyramidStore] Loaded ${pyramidsResult.data?.length || 0} pyramids, ${itemsResult.data?.length || 0} items`);
  },
  
  syncPyramidToSupabase: async (pyramid: Pyramid) => {
    if (!isSupabaseAvailable()) {
      return { success: false, error: 'Supabase not available' };
    }
    
    // Use upsert to handle both new and existing pyramids
    const result = await upsertRecord(PYRAMIDS_TABLE, pyramid);
    if (result.error) {
      console.error('[PyramidStore] syncPyramidToSupabase error:', result.error);
    }
    return { success: !result.error, error: result.error || undefined };
  },
  
  syncItemToSupabase: async (item: PyramidItem) => {
    if (!isSupabaseAvailable()) {
      return { success: false, error: 'Supabase not available' };
    }
    
    // Use upsert to handle both new and existing items
    const result = await upsertRecord(ITEMS_TABLE, item);
    if (result.error) {
      console.error('[PyramidStore] syncItemToSupabase error:', result.error);
    }
    return { success: !result.error, error: result.error || undefined };
  },
  
  // ========================================================================
  // Pyramid CRUD
  // ========================================================================
  
  createPyramid: async (data) => {
    const id = generateUUID();
    const now = new Date();
    
    const pyramid: Pyramid = {
      id,
      name: data.name,
      description: data.description,
      categories: data.categories || ['Process Automation', 'Tech Debt', 'Defects', 'Other Priorities'],
      createdBy: data.createdBy,
      workspaceId: data.workspaceId,
      isPublished: false,
      createdAt: now,
      updatedAt: now,
    };
    
    set(state => ({
      pyramids: [...state.pyramids, pyramid],
      activePyramidId: id,
    }));
    
    // Sync to Supabase
    const result = await get().syncPyramidToSupabase(pyramid);
    if (result.error) {
      console.error('[PyramidStore] Failed to sync pyramid:', result.error);
      dispatchStoreError('Pyramid', 'create pyramid', result.error);
    }
    
    return id;
  },
  
  updatePyramid: async (id, updates) => {
    const pyramid = get().pyramids.find(p => p.id === id);
    if (!pyramid) return;
    
    const updatedPyramid = {
      ...pyramid,
      ...updates,
      updatedAt: new Date(),
    };
    
    set(state => ({
      pyramids: state.pyramids.map(p => p.id === id ? updatedPyramid : p),
    }));
    
    await get().syncPyramidToSupabase(updatedPyramid);
  },
  
  deletePyramid: async (id) => {
    set(state => ({
      pyramids: state.pyramids.filter(p => p.id !== id),
      items: state.items.filter(i => i.pyramidId !== id),
      activePyramidId: state.activePyramidId === id ? null : state.activePyramidId,
    }));
    
    if (isSupabaseAvailable()) {
      const result = await deleteRecord(PYRAMIDS_TABLE, id);
      if (result.error) {
        console.error('[PyramidStore] Failed to delete pyramid:', result.error);
        dispatchStoreError('Pyramid', 'delete pyramid', result.error);
      }
    }
  },
  
  setActivePyramid: (id) => {
    set({ activePyramidId: id });
  },
  
  // ========================================================================
  // Item CRUD
  // ========================================================================
  
  addItem: async (pyramidId, data) => {
    const id = generateUUID();
    const now = new Date();
    
    // Calculate next rank for this cell
    const cellItems = get().items.filter(
      i => i.pyramidId === pyramidId && 
           i.timeHorizon === data.timeHorizon && 
           i.category === data.category
    );
    const nextRank = cellItems.length > 0 ? Math.max(...cellItems.map(i => i.rank)) + 1 : 1;
    
    const item: PyramidItem = {
      id,
      pyramidId,
      name: data.name,
      description: data.description,
      timeHorizon: data.timeHorizon,
      category: data.category,
      rank: nextRank,
      createdBy: data.createdBy,
      source: data.source || 'manual',
      createdAt: now,
      updatedAt: now,
    };
    
    set(state => ({
      items: [...state.items, item],
    }));
    
    // Update pyramid's updatedAt
    await get().updatePyramid(pyramidId, {});
    
    // Sync item to Supabase
    const result = await get().syncItemToSupabase(item);
    if (result.error) {
      console.error('[PyramidStore] Failed to sync item:', result.error);
      dispatchStoreError('Pyramid', 'add item', result.error);
    }
    
    return id;
  },
  
  updateItem: async (itemId, updates) => {
    const item = get().items.find(i => i.id === itemId);
    if (!item) return;
    
    const updatedItem = {
      ...item,
      ...updates,
      updatedAt: new Date(),
    };
    
    set(state => ({
      items: state.items.map(i => i.id === itemId ? updatedItem : i),
    }));
    
    await get().syncItemToSupabase(updatedItem);
    
    // Update pyramid's updatedAt
    if (item.pyramidId) {
      await get().updatePyramid(item.pyramidId, {});
    }
  },
  
  deleteItem: async (itemId) => {
    const item = get().items.find(i => i.id === itemId);
    
    set(state => ({
      items: state.items.filter(i => i.id !== itemId),
    }));
    
    if (isSupabaseAvailable()) {
      await deleteRecord(ITEMS_TABLE, itemId);
    }
    
    // Update pyramid's updatedAt
    if (item?.pyramidId) {
      await get().updatePyramid(item.pyramidId, {});
    }
  },
  
  moveItem: async (itemId, timeHorizon, category, newRank) => {
    const item = get().items.find(i => i.id === itemId);
    if (!item) return;
    
    // Calculate rank if not provided
    let rank = newRank;
    if (rank === undefined) {
      const cellItems = get().items.filter(
        i => i.pyramidId === item.pyramidId && 
             i.timeHorizon === timeHorizon && 
             i.category === category &&
             i.id !== itemId
      );
      rank = cellItems.length > 0 ? Math.max(...cellItems.map(i => i.rank)) + 1 : 1;
    }
    
    await get().updateItem(itemId, { timeHorizon, category, rank });
  },
  
  updateItemRank: async (itemId, newRank) => {
    await get().updateItem(itemId, { rank: newRank });
  },
  
  // ========================================================================
  // Category Management
  // ========================================================================
  
  updateCategories: async (pyramidId, categories) => {
    await get().updatePyramid(pyramidId, { categories });
  },
  
  // ========================================================================
  // Bulk Operations
  // ========================================================================
  
  importItems: async (pyramidId, items, createdBy) => {
    const now = new Date();
    const newItems: PyramidItem[] = items.map((item, index) => ({
      id: generateUUID(),
      pyramidId,
      name: item.name,
      description: item.description,
      timeHorizon: item.timeHorizon,
      category: item.category,
      rank: item.rank ?? index + 1,
      createdBy,
      source: item.source || 'imported',
      linkedCandidateId: item.linkedCandidateId,
      externalId: item.externalId,
      metadata: item.metadata,
      createdAt: now,
      updatedAt: now,
    }));
    
    set(state => ({
      items: [...state.items, ...newItems],
    }));
    
    // Sync all items to Supabase
    for (const item of newItems) {
      await get().syncItemToSupabase(item);
    }
    
    // Update pyramid's updatedAt
    await get().updatePyramid(pyramidId, {});
  },
  
  clearItems: async (pyramidId) => {
    const itemsToDelete = get().items.filter(i => i.pyramidId === pyramidId);
    
    set(state => ({
      items: state.items.filter(i => i.pyramidId !== pyramidId),
    }));
    
    if (isSupabaseAvailable()) {
      for (const item of itemsToDelete) {
        await deleteRecord(ITEMS_TABLE, item.id);
      }
    }
  },
  
  // ========================================================================
  // Getters
  // ========================================================================
  
  getPyramidById: (id) => {
    return get().pyramids.find(p => p.id === id);
  },
  
  getItemsForPyramid: (pyramidId) => {
    return get().items
      .filter(i => i.pyramidId === pyramidId)
      .sort((a, b) => a.rank - b.rank);
  },
  
  getItemsForCell: (pyramidId, timeHorizon, category) => {
    return get().items
      .filter(i => 
        i.pyramidId === pyramidId && 
        i.timeHorizon === timeHorizon && 
        i.category === category
      )
      .sort((a, b) => a.rank - b.rank);
  },
  
  getActivePyramid: () => {
    const { activePyramidId, pyramids } = get();
    return activePyramidId ? pyramids.find(p => p.id === activePyramidId) : undefined;
  },
}));

export default usePyramidStore;
